import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Sparkles, Calendar, Gift, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";

export default function Overview() {
  const tr = useTr();
  const [analytics, setAnalytics] = useState(null);
  const [pending, setPending] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [sub, setSub] = useState(null);
  const [ref, setRef] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setAnalytics(data));
    api.get("/posts", { params: { status: "pending" } }).then(({ data }) => setPending(data));
    api.get("/calendar").then(({ data }) => setCalendar(data));
    api.get("/billing/subscription").then(({ data }) => setSub(data));
    api.get("/referrals/me").then(({ data }) => setRef(data));
  }, []);

  const totals = analytics?.totals;
  const pct = (used, max) => (max == null || max === 0 ? 0 : Math.min(100, Math.round((used / max) * 100)));

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("OPERACIÓN · PANEL CENTRAL")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Hola,")} {user?.name?.split(" ")[0] || tr("agente")}.</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/dashboard/generator" className="font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-4 py-3 hover-brutal border border-[#0A0A0A] flex items-center gap-2" data-testid="overview-generate">
            <Sparkles className="w-3 h-3" /> {tr("GENERAR CONTENIDO")}
          </Link>
          <Link to="/dashboard/calendar" className="font-mono-mark text-[10px] bg-white text-[#0A0A0A] px-4 py-3 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center gap-2" data-testid="overview-calendar">
            <Calendar className="w-3 h-3" /> {tr("VER CALENDARIO")}
          </Link>
        </div>
      </div>

      {/* Referral Banner */}
      {ref && (
        <Link to="/dashboard/referrals" className="block bg-[#FFD600] border border-[#0A0A0A] hover-brutal p-5 md:p-6" data-testid="overview-ref-banner">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#0A0A0A] text-[#FFD600] flex items-center justify-center">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <div className="font-mono-mark text-[10px]">{tr("PROGRAMA DE REFERIDOS")}</div>
                <div className="font-display text-xl md:text-2xl leading-tight">
                  {tr("Invita y gana")} <span className="bg-[#0A0A0A] text-[#FFD600] px-2">{tr("30 días Pro")}</span> {tr("gratis. Tu código:")} {ref.code}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <div className="font-mono-mark text-[10px]">{tr("INVITADOS")}</div>
                <div className="font-display text-3xl font-mono-data">{ref.count}</div>
              </div>
              <div>
                <div className="font-mono-mark text-[10px]">{tr("DÍAS BONUS")}</div>
                <div className="font-display text-3xl font-mono-data">{ref.bonus_days_remaining}</div>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Plan Usage */}
      {sub && (
        <div className="bg-white border border-[#0A0A0A] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-[#E53935]" />
              <div>
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PLAN ACTIVO")}</div>
                <div className="font-display text-2xl uppercase">{sub.plan}</div>
              </div>
            </div>
            {sub.plan !== "agency" && (
              <Link to="/dashboard/subscription" className="font-mono-mark text-[10px] bg-[#E53935] text-white px-3 py-2 border border-[#E53935]" data-testid="overview-upgrade">
                {tr("MEJORAR PLAN")}
              </Link>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-2">
            <UsageBar label={tr("POSTS GEN.")} used={sub.usage.posts} max={sub.limits.posts} />
            <UsageBar label={tr("IMÁGENES")} used={sub.usage.images} max={sub.limits.images} />
            <UsageBar label={tr("REDES")} used={sub.usage.networks} max={sub.limits.networks} />
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#0A0A0A]">
        <Kpi label={tr("SEGUIDORES")} value={totals?.followers} accent />
        <Kpi label={tr("IMPRESIONES")} value={totals?.impressions} border />
        <Kpi label={tr("ENGAGEMENT")} value={totals ? totals.engagement_rate + "%" : null} border />
        <Kpi label={tr("POSTS PUBLICADOS")} value={totals?.posts_published} border />
      </div>

      {/* Split */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#0A0A0A] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("COLA · PENDIENTES DE APROBACIÓN")}</div>
            <Link to="/dashboard/feed" className="font-mono-mark text-[10px] underline decoration-[#E53935] underline-offset-4" data-testid="overview-feed-link">{tr("VER TODO")}</Link>
          </div>
          {pending.length === 0 ? (
            <EmptyState msg={tr("Sin posts pendientes. La IA aún no ha generado nada. Ve al Generador.")} />
          ) : (
            <ul className="divide-y divide-[#0A0A0A]">
              {pending.slice(0, 5).map((p) => (
                <li key={p.id} className="py-3 flex items-start gap-4">
                  <span className="font-mono-mark text-[10px] text-[#E53935] w-16 shrink-0">{p.network.toUpperCase()}</span>
                  <p className="text-sm flex-1 line-clamp-2">{p.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#0A0A0A] p-6">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-4">{tr("AGENDA · PRÓXIMOS")}</div>
          {calendar.length === 0 ? (
            <EmptyState msg={tr("Nada programado.")} />
          ) : (
            <ul className="space-y-3">
              {calendar.slice(0, 6).map((p) => (
                <li key={p.id} className="flex items-start gap-3">
                  <div className="border border-[#0A0A0A] text-center w-12 p-1 shrink-0">
                    <div className="font-mono-mark text-[9px] text-[#5C5C5C]">{new Date(p.scheduled_at).toLocaleString("es-MX", { month: "short" }).toUpperCase()}</div>
                    <div className="font-display text-xl leading-none">{new Date(p.scheduled_at).getDate()}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono-mark text-[9px] text-[#E53935]">{p.network.toUpperCase()}</div>
                    <div className="text-sm line-clamp-2">{p.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function UsageBar({ label, used, max }) {
  const percent = max == null ? 0 : Math.min(100, (used / max) * 100);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}</span>
        <span className="font-mono-data text-sm">
          {used} / {max == null ? "∞" : max}
        </span>
      </div>
      <div className="h-2 bg-[#E5E5E5] relative">
        <div className={`absolute top-0 left-0 bottom-0 ${percent > 80 ? "bg-[#E53935]" : "bg-[#0A0A0A]"}`} style={{ width: `${max == null ? 5 : percent}%` }} />
      </div>
    </div>
  );
}

function Kpi({ label, value, border, accent }) {
  return (
    <div className={`p-6 ${border ? "border-l border-[#0A0A0A]" : ""} ${accent ? "bg-[#0A0A0A] text-white" : "bg-white"}`}>
      <div className={`font-mono-mark text-[10px] ${accent ? "text-[#FFD600]" : "text-[#5C5C5C]"}`}>{label}</div>
      <div className="font-display text-4xl mt-3 font-mono-data">
        {value == null ? <span className="inline-block w-20 h-8 bg-[#E5E5E5] redact-pulse" /> : value.toLocaleString?.("es-MX") ?? value}
      </div>
    </div>
  );
}

function EmptyState({ msg }) {
  const tr = useTr();
  return (
    <div className="border border-dashed border-[#0A0A0A] p-8 text-center">
      <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("SIN REGISTROS")}</div>
      <div className="text-sm mt-2">{msg}</div>
    </div>
  );
}
